import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TABS, TabId } from './tabConfig';
import { cn } from '@/lib/utils';

interface TabNavProps {
  activeTab: TabId;
  activeIndex: number;
  onTabChange: (tabId: TabId, direction: 'forward' | 'backward') => void;
}

const TabNav: React.FC<TabNavProps> = ({ activeTab, activeIndex, onTabChange }) => {
  const navRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleTabClick = (id: TabId, index: number) => {
    const direction = index > activeIndex ? 'forward' : 'backward';
    onTabChange(id, direction);
    window.location.hash = `#${id}`;
    setMenuOpen(false);
    navRef.current
      ?.querySelector(`[data-tab="${id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const mainTabs = TABS.filter((t) => t.id !== 'login');
  const loginTab = TABS.find((t) => t.id === 'login')!;

  return (
    <>
    <header
      className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center px-4 md:px-8"
      style={{
        background: 'rgba(10, 10, 10, 0.88)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      {/* Left — wordmark */}
      <div className="flex-shrink-0 flex items-center gap-2 mr-4 md:mr-8">
        <img src="https://crossroadsdatarepocdn-prod.sgp1.cdn.digitaloceanspaces.com/logo-black.png" alt="Crossroads Church" className="h-8" style={{ filter: 'invert(1)' }} />
      </div>

      {/* Desktop — tab bar */}
      <nav
        ref={navRef}
        className="hidden md:flex flex-1 items-center gap-0 overflow-x-auto no-scrollbar"
        role="tablist"
        aria-label="Site sections"
        style={{ scrollbarWidth: 'none' }}
      >
        {mainTabs.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              data-tab={tab.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => handleTabClick(tab.id, tab.index)}
              className={cn(
                'relative flex-shrink-0 px-3 md:px-4 py-1 text-cr-white font-haffer font-light uppercase tracking-widest transition-opacity duration-300',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-cr-accent',
                isActive ? 'opacity-100' : 'opacity-40 hover:opacity-70'
              )}
              style={{ fontSize: '0.65rem', letterSpacing: '0.2em' }}
            >
              {tab.label}
              {isActive && (
                <motion.span
                  layoutId="tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-cr-accent"
                  transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                />
              )}
            </button>
          );
        })}
      </nav>

      {/* Desktop — Login tab (far right) */}
      {(() => {
        const isActive = activeTab === 'login';
        return (
          <button
            data-tab="login"
            role="tab"
            aria-selected={isActive}
            onClick={() => handleTabClick('login', loginTab.index)}
            className={cn(
              'hidden md:block relative flex-shrink-0 ml-4 md:ml-6 px-3 md:px-4 py-1 text-cr-white font-haffer font-light uppercase tracking-widest transition-opacity duration-300',
              'focus:outline-none focus-visible:ring-1 focus-visible:ring-cr-accent',
              isActive ? 'opacity-100' : 'opacity-40 hover:opacity-70'
            )}
            style={{ fontSize: '0.65rem', letterSpacing: '0.2em' }}
          >
            {loginTab.label}
            {isActive && (
              <motion.span
                layoutId="tab-underline"
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-cr-accent"
                transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
              />
            )}
          </button>
        );
      })()}

      {/* Mobile — active tab label + hamburger */}
      <div className="flex md:hidden flex-1 items-center justify-between">
        <span
          className="font-haffer font-light uppercase tracking-widest text-cr-white/60"
          style={{ fontSize: '0.65rem', letterSpacing: '0.2em' }}
        >
          {TABS.find((t) => t.id === activeTab)?.label ?? ''}
        </span>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          className="w-10 h-10 flex flex-col items-center justify-center gap-[5px] focus:outline-none"
        >
          <motion.span
            className="block h-px bg-cr-white origin-center"
            style={{ width: 22 }}
            animate={menuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
          />
          <motion.span
            className="block h-px bg-cr-white origin-center"
            style={{ width: 22 }}
            animate={menuOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.2 }}
          />
          <motion.span
            className="block h-px bg-cr-white origin-center"
            style={{ width: 22 }}
            animate={menuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
          />
        </button>
      </div>
    </header>

    {/* Mobile drawer */}
    <AnimatePresence>
      {menuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40 md:hidden"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setMenuOpen(false)}
          />
          {/* Drawer */}
          <motion.nav
            className="fixed top-16 left-0 right-0 z-40 md:hidden flex flex-col"
            style={{
              background: 'rgba(10,10,10,0.97)',
              backdropFilter: 'blur(24px)',
              borderBottom: '1px solid var(--color-border)',
            }}
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            role="tablist"
            aria-label="Site sections"
          >
            {[...mainTabs, loginTab].map((tab, i) => {
              const isActive = tab.id === activeTab;
              return (
                <motion.button
                  key={tab.id}
                  data-tab={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleTabClick(tab.id, tab.index)}
                  className={cn(
                    'relative w-full text-left px-8 py-5 font-haffer font-light uppercase tracking-widest transition-colors',
                    'focus:outline-none',
                    isActive ? 'text-cr-white' : 'text-cr-white/40 hover:text-cr-white/70'
                  )}
                  style={{
                    fontSize: '0.75rem',
                    letterSpacing: '0.25em',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05, ease: [0.76, 0, 0.24, 1] }}
                >
                  {isActive && (
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[2px]"
                      style={{ background: 'var(--color-accent)' }}
                    />
                  )}
                  {tab.label}
                </motion.button>
              );
            })}
          </motion.nav>
        </>
      )}
    </AnimatePresence>
    </>
  );
};

export default TabNav;
